import { HiddenAxios } from '@/functions/axios-functions'
import { PromotionType, PaymentType } from '@/states/promotion-state';
import { SettingsContext } from '@/utilities/settings-context';

type SubscriptionCapabilityBase = {
    description: string;
    type: string;
};

type MonthlyCreditsCapability = SubscriptionCapabilityBase & {
    type: 'MonthlyCreditsCapability';
    credits: number;
    promotionType: Exclude<PromotionType, 'none'>;
    lastApplied?: string;
};

type PriceChangeCapability = SubscriptionCapabilityBase & {
    type: 'PriceChangeCapability';
    packagePrices: Record<PaymentType, Record<Exclude<PromotionType, 'none'>, number>>;
};

type SubscriptionPeriodBase = {
    type: string;
    startDate: string;
};

export type TimeLimitedSubscriptionPeriod = SubscriptionPeriodBase & {
    type: 'TimeLimitedSubscriptionPeriod';
    endDate: string;
};

export type InfiniteSubscriptionPeriod = SubscriptionPeriodBase & {
    type: 'InfiniteSubscriptionPeriod';
};

export type SubscriptionCapability = MonthlyCreditsCapability | PriceChangeCapability;
export type SubscriptionPeriod = TimeLimitedSubscriptionPeriod | InfiniteSubscriptionPeriod;

export type SubscriptionTemplate = {
    id: string;
    name: string;
    price: number;
    capabilities: SubscriptionCapability[];
    period: SubscriptionPeriod;
};

export type CompanySubscription = SubscriptionTemplate & {
    id: string;
    isExpired: boolean;
    companyVolumePackageIsLight: boolean;
    invoiceCreationPeriod: InvoiceCreationPeriod;
    enableCostCenterInput?: boolean;
};

export enum InvoiceCreationPeriod {
    none = 'none',
    weekly = 'weekly',
    semiMonthly = 'semiMonthly',
    monthly = 'monthly',
    quarterly = 'quarterly'
}

export type AddSubscriptionRequest = {
    name: string;
    price: number;
    capabilities: SubscriptionCapability[];
    period: SubscriptionPeriod;
    invoiceCreationPeriod: InvoiceCreationPeriod;
    enableCostCenterInput?: boolean;
};


export const getCompanySubscriptions = async (companyId: number): Promise<CompanySubscription[]> => {
    const { data } = await HiddenAxios.get<CompanySubscription[]>(`${SettingsContext.getApiUrl('subscriptions')}/${companyId}`)
    return data;
}

export const getSubscriptionTemplates = async (): Promise<SubscriptionTemplate[]> => {
    const { data } = await HiddenAxios.get<SubscriptionTemplate[]>(SettingsContext.getApiUrl('subscriptions'))
    return data;
}

export const expireCompanySubscription = async (subscriptionId: string): Promise<void> => {
    await HiddenAxios.put(`${SettingsContext.getApiUrl('expireSubscription')}/${subscriptionId}`);
}

export const addCompanySubscription = async (
    companyId: number,
    payload: AddSubscriptionRequest
): Promise<void> => {
    await HiddenAxios.post(`${SettingsContext.getApiUrl('subscriptions')}/${companyId}`, payload)
};
